<template>
  <div class="unloading-errors-section">
    <div v-if="!farmId" class="info-state">
      <div class="info-icon-container">
        <svg class="info-svg">
          <use xlink:href="/svg/sprite.svg#icon-cow" />
        </svg>
      </div>
      <div class="info-content">
        <h4>{{ 'color_indication.unloading_errors.no_farm_title' | translate }}</h4>
        <p>{{ 'color_indication.unloading_errors.no_farm_description' | translate }}</p>
      </div>
    </div>

    <div v-else-if="loading" class="loading-state">
      {{ 'color_indication.unloading_errors.loading' | translate }}
    </div>

    <template v-else>
      <div class="section-header">
        <p class="section-description">
          {{ 'color_indication.unloading_errors.description' | translate }}
        </p>
      </div>

      <div v-if="groups.length === 0" class="empty-state">
        <p>{{ 'color_indication.unloading_errors.empty_state' | translate }}</p>
      </div>

      <div v-else>
        <!-- Header -->
        <div class="grid-header">
          <div class="grid-header-item header-checkbox">
            <v-switch 
              v-model="selectAll"
              @input="handleSelectAllChange"
              :label="$options.filters.translate('color_indication.unloading_errors.select_all')">
            </v-switch>
          </div>
          <div class="grid-header-item header-with-tooltips">
            <div class="tooltip-wrapper">
              <svg class="info-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                @mouseenter="showTooltip('percent')" @mouseleave="hideTooltip">
                <circle cx="12" cy="12" r="10" stroke-width="2" />
                <path d="M12 16v-4M12 8h.01" stroke-width="2" stroke-linecap="round" />
              </svg>
              <div v-if="activeTooltip === 'percent'" class="tooltip">
                {{ 'color_indication.unloading_errors.tooltip_percent' | translate }}
              </div>
            </div>

            <span class="header-separator"></span>

            <div class="tooltip-wrapper">
              <svg class="info-icon-small" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                @mouseenter="showTooltip('weight')" @mouseleave="hideTooltip">
                <circle cx="12" cy="12" r="10" stroke-width="2" />
                <path d="M12 16v-4M12 8h.01" stroke-width="2" stroke-linecap="round" />
              </svg>
              <div v-if="activeTooltip === 'weight'" class="tooltip">
                {{ 'color_indication.unloading_errors.tooltip_weight' | translate }}
              </div>
            </div>
          </div>
        </div>

        <!-- Groups -->
        <div v-for="group in groups" :key="group.id" class="grid-row">
          <div class="grid-cell-label">
            <v-switch 
              v-model="group.enabled"
              @input="handleToggleChange(group)">
            </v-switch>
            <span class="group-name">{{ group.group && group.group.name || $options.filters.translate('color_indication.unloading_errors.no_name') }}</span>
          </div>

          <div class="grid-cell-buttons" :class="{ 'is-disabled': !group.enabled }">
            <button class="value-button" :class="{ 'is-active': group.enabled && group.level === 'percent' }"
              :disabled="!group.enabled" @click="selectType(group, 'percent')">
              <input type="number" v-model.number="group.percent" :disabled="!group.enabled"
                class="value-input" min="0" max="100" step="0.1" @click.stop @focus="handleFocus(group, 'percent')"
                @blur="handleBlur(group, 'percent')" @input="handleInput(group, 'percent')" />
              <span class="value-unit">{{ 'color_indication.unloading_errors.unit_percent' | translate }}</span>
            </button>

            <button class="value-button" :class="{ 'is-active': group.enabled && group.level === 'weight' }"
              :disabled="!group.enabled" @click="selectType(group, 'weight')">
              <input type="number" v-model.number="group.weight" :disabled="!group.enabled"
                class="value-input" min="0" max="9999" step="0.1" @click.stop @focus="handleFocus(group, 'weight')"
                @blur="handleBlur(group, 'weight')" @input="handleInput(group, 'weight')" />
              <span class="value-unit">{{ 'color_indication.unloading_errors.unit_weight' | translate }}</span>
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'UnloadingErrorsTab',

  props: {
    active: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      loading: false,
      groups: [],
      dataLoaded: false,
      isInitializing: true,
      initialData: null,
      activeTooltip: null,
      selectAll: false,
      focusedValues: {}
    }
  },

  computed: {
    farmId () {
      return this.$route.params.farm_id
    }
  },

  watch: {
    active (newVal) {
      if (newVal && this.farmId && !this.dataLoaded) {
        this.loadGroups()
      }
    },

    farmId (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.dataLoaded = false
        this.groups = []

        if (this.active && newVal) {
          this.loadGroups()
        }
      }
    },

    groups: {
      handler () {
        this.updateSelectAllState()
      },
      deep: true
    }
  },

  methods: {
    showTooltip (type) {
      this.activeTooltip = type
    },

    hideTooltip () {
      this.activeTooltip = null
    },

    updateSelectAllState () {
      if (this.groups.length === 0) {
        this.selectAll = false
        return
      }

      const allEnabled = this.groups.every(group => group.enabled)

      this.$nextTick(() => {
        this.selectAll = allEnabled
      })
    },

    handleSelectAllChange () {
      if (this.isInitializing) return

      const newState = this.selectAll

      this.groups.forEach((group, index) => {
        this.$set(this.groups[index], 'enabled', newState)

        if (!newState) {
          this.$set(this.groups[index], 'level', null)
        } else {
          this.$set(this.groups[index], 'level', 'percent')
          if (!this.groups[index].percent || this.groups[index].percent === 0) {
            this.$set(this.groups[index], 'percent', 3)
          }
        }
      })

      this.$nextTick(() => {
        this.saveBulkGroups()
      })
    },

    async saveBulkGroups () {
      try {
        const bulkData = this.groups.map(group => ({
          id: group.id,
          level: group.enabled ? group.level : null,
          percent: group.percent || null,
          weight: group.weight || null
        }))

        await api.monitoring.updateGroupsBulk(bulkData)

        this.$toasted.show('✓ ' + this.$options.filters.translate('color_indication.messages.settings_saved'), {
          duration: 2000,
          position: 'bottom-right',
          type: 'success',
          theme: 'bubble'
        })

        this.saveInitialState()
      } catch (error) {
        console.error('❌ Error saving groups:', error)
        this.$toasted.error(this.$options.filters.translate('color_indication.messages.save_error'))
      }
    },

    async loadGroups () {
      if (!this.farmId) return

      this.loading = true
      this.isInitializing = true

      try {
        const { data } = await api.monitoring.groups({
          farm: this.farmId
        })

        let groupsData = []
        if (data.data && Array.isArray(data.data)) {
          groupsData = data.data
        } else if (data.results && Array.isArray(data.results)) {
          groupsData = data.results
        } else if (Array.isArray(data)) {
          groupsData = data
        }

        this.groups = groupsData.map(item => ({
          id: item.id,
          group: item.group || { id: null, name: this.$options.filters.translate('color_indication.unloading_errors.no_name') },
          level: item.level,
          percent: item.percent || 0,
          weight: item.weight || 0,
          enabled: item.level !== null
        }))

        this.saveInitialState()
        this.dataLoaded = true
        this.updateSelectAllState()

        setTimeout(() => {
          this.isInitializing = false
        }, 500)
      } catch (error) {
        console.error('❌ Error loading groups:', error)
        this.$toasted.error(this.$options.filters.translate('color_indication.messages.load_groups_error'))
      } finally {
        this.loading = false
      }
    },

    saveInitialState () {
      this.initialData = JSON.stringify(this.groups)
    },

    hasChanges () {
      if (!this.initialData) return true
      return JSON.stringify(this.groups) !== this.initialData
    },

    handleToggleChange (group) {
      if (this.isInitializing) return

      if (!group.enabled) {
        this.$set(group, 'level', null)
      } else {
        this.$set(group, 'level', 'percent')
        if (!group.percent || group.percent === 0) {
          this.$set(group, 'percent', 3)
        }
      }

      this.$nextTick(() => {
        this.updateSelectAllState()
        this.saveGroup(group)
      })
    },

    selectType (group, type) {
      if (!group.enabled) return

      this.$set(group, 'level', type)
      this.saveGroup(group)
    },

    handleFocus (group, type) {
      if (!group.enabled) return

      this.focusedValues = {
        id: group.id,
        type: type,
        value: group[type],
        level: group.level
      }

      this.$set(group, 'level', type)
    },

    handleInput (group, type) {
      if (!group.enabled) return

      if (type === 'percent' && group[type] > 100) {
        this.$set(group, type, 100)
      }
      if (type === 'weight' && group[type] > 9999) {
        this.$set(group, type, 9999)
      }
      if (group[type] < 0) {
        this.$set(group, type, 0)
      }
    },

    handleBlur (group, type) {
      if (this.isInitializing || !group.enabled) return

      let value = group[type] || 0

      if (type === 'percent') {
        value = Math.min(100, Math.max(0, value))
      } else {
        value = Math.min(9999, Math.max(0, value))
      }

      this.$set(group, type, value)
      this.$set(group, 'level', type)

      const hasChanges = this.focusedValues.id === group.id && (
        this.focusedValues.value !== value ||
        this.focusedValues.level !== type
      )

      if (hasChanges) {
        this.saveGroup(group)
      }

      this.focusedValues = {}
    },

    async saveGroup (group) {
      try {
        const payload = {
          level: group.enabled ? group.level : null,
          percent: group.percent || null,
          weight: group.weight || null
        }

        await api.monitoring.updateGroup(group.id, payload)

        this.$toasted.show('✓ ' + this.$options.filters.translate('color_indication.messages.settings_saved'), {
          duration: 2000,
          position: 'bottom-right',
          type: 'success',
          theme: 'bubble'
        })

        this.saveInitialState()
      } catch (error) {
        console.error('❌ Error saving group:', error)
        this.$toasted.error(this.$options.filters.translate('color_indication.messages.save_error'))
      }
    }
  }
}
</script>

<style scoped>
.unloading-errors-section {
  padding-right: 40px;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #666;
}

.info-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
}

.info-icon-container {
  margin-bottom: 20px;
}

.info-svg {
  width: 64px;
  height: 64px;
  opacity: 0.8;
  color: #4CAF50;
}

.info-content {
  max-width: 450px;
}

.info-content h4 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
}

.info-content p {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin: 0;
}

.section-header {
  margin-bottom: 40px;
}

.section-description {
  font-size: 14px;
  line-height: 1.6;
  color: #000;
  margin: 0;
}

.empty-state {
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.grid-header {
  display: grid;
  grid-template-columns: 1fr 155px;
  gap: 16px;
  margin-bottom: 8px;
  align-items: center;
  padding: 10px 0;
}

.grid-header-item {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.grid-header-item.header-checkbox {
  justify-content: flex-start;
  gap: 0;
}

.header-with-tooltips {
  display: flex;
  align-items: center;
  gap: 24px;
}

.tooltip-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.info-icon-small {
  width: 24px;
  height: 24px;
  color: #1DA643;
  cursor: help;
  transition: color 0.2s;
}

.info-icon-small:hover {
  color: #45a049;
}

.tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid #D7D7D7;
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 160px;
  font-size: 12px;
  line-height: 1.5;
  color: #000;
  pointer-events: none;
  white-space: normal;
}

.tooltip::before {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 6px 6px 0 6px;
  border-color: white transparent transparent transparent;
}

.tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 1px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 7px 7px 0 7px;
  border-color: #D7D7D7 transparent transparent transparent;
  z-index: -1;
}

.grid-row {
  display: grid;
  grid-template-columns: 1fr 155px;
  gap: 16px;
  align-items: center;
  padding: 10px 0;
  font-size: 14px;
  transition: background-color 0.2s;
  border-radius: 8px;
}

.grid-cell-label {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
}

.group-name {
  flex: 1;
  font-size: 16px;
  font-weight: 900;
  color: #000;
  margin-top: 4px;
}

.grid-cell-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.4rem;
  background: #EEEEEE;
  border-radius: 1rem;
}

.grid-cell-buttons.is-disabled {
  background: transparent;
}

.value-button {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 4px;
  background: #EEEEEE;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 70px;
  justify-content: center;
  position: relative;
}

.value-button:disabled {
  cursor: not-allowed;
  background: transparent;
}

.value-button.is-active {
  background: #59CC56;
  border-color: #59CC56;
}

.value-input {
  width: 40px;
  background: transparent;
  border: none;
  outline: none;
  font-size: 18px;
  font-weight: 700;
  color: #000;
  text-align: center;
  padding: 0;
  transition: color 0.2s;
}

.value-button.is-active .value-input {
  color: #000;
}

.value-input:disabled {
  color: #979797;
}

.value-input::-webkit-inner-spin-button,
.value-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.value-input[type=number] {
  -moz-appearance: textfield;
}

.value-unit {
  font-size: 12px;
  font-weight: 700;
  color: #707070;
  transition: color 0.2s;
}

.value-button:disabled .value-unit {
  color: #979797;
}
.v-switch-wrapper--with-label {
  justify-content: unset;
}
</style>